import React, {
	ColumnDef,
	useReactTable,
	getCoreRowModel,
	Column,
	Row,
	CellContext,
} from '@tanstack/react-table'
import { RefObject, useMemo, useEffect, ReactNode } from 'react'
import {
	CopyAllOutlined,
	Delete,
	DriveFileMoveOutlined,
	Edit,
	FileDownloadOutlined,
	FilterAltOff,
	InfoOutlined,
	Key,
	KeyboardArrowDown,
	KeyboardArrowUp,
	LocalOffer,
	Lock,
	ManageHistory,
	Star,
	StarOutlineOutlined,
	UnfoldMore,
	Visibility,
	IosShareOutlined,
	StickyNote2Sharp,
	Link,
	Refresh,
	AutoAwesome,
} from '@mui/icons-material'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { DocumentData } from '../../lib/api/types/document.interfaces'
import {
	Checkbox,
	EllipsisText,
	IconButton,
	OptionsMenuButton,
	Tooltip,
} from '../../components/ui'
import { openAuraEdit, useToggleObjectFavorite } from '../api/documentHooks'
import cn from '../../lib/utils/cn'
import { isFileNameEsignSupported } from '../../lib/utils/esign'
import { MainLayoutType } from '../../components/layouts/MainLayout'
import {
	EditingItem,
	DataTableEntry,
	OrderDirectionType,
	OrderByType,
	CustomColumnFilter,
	WorkspaceType,
} from '../../pages/Workspace/components/interfaces.table'
import {
	formatDocumentSize,
	processingStatuses,
	shortenFileName,
} from '../../lib/utils/formatDocument'
import { useUserDateFormat } from '../useUserDateFormat'
import FilterButton, {
	FILTERED_OBJECT_TYPE_VALUES,
} from '../../components/table/FilterButton'
import ProcessingStatusIcon from '../../pages/Workspace/components/ProcessingStatusIcon'
import { FilteredObjectType } from '../../components/table/FilterButton'
import FileFormatIcon from '../../components/common/FileFormatIcon'
import { Features, useBilling } from '../../context/billingContext'
import { FILTER_TYPES } from './useColumnFilters'
import { useAuth } from '../../hooks'
//icons
import { ReactComponent as ResetColumnsSize } from '../../assets/icons/table/reset-columns.svg'
import { ReactComponent as SigningSymbol } from '../../assets/icons/interactions/signing-symbol.svg'

const ProcessingStatusCell = ({
	row,
}: CellContext<DataTableEntry, unknown>) => {
	const { objectType, processingStatus, size } = row.original
	return (
		<ProcessingStatusIcon
			status={Number(processingStatus)}
			isEmptyFile={!size}
			objectType={objectType}
		/>
	)
}

interface useCreateDefaultDataTableProps {
	data?: DocumentData[] | null
	currentFolderId?: string | null
	visibleColumns: string[]
	selectedMobileColumn?: string
	containerRef?: RefObject<HTMLDivElement>
	orderBy?: OrderByType
	orderDirection?: OrderDirectionType
	onToggleLock: (id: string, value: boolean) => void
	onToggleItem?: (
		id: string,
		type: number,
		checked: boolean,
		name?: string,
		fileType?: string,
		isFavorite?: boolean,
	) => void
	onToggleAll?: (
		ids: { id: string; type: number; name?: string; isFavorite?: boolean }[],
		checked: boolean,
	) => void
	onDeleteItem: (item: EditingItem) => void
	onEditItem: (item: EditingItem) => void
	onPreview: (id: string, versionId?: string | null, isBlank?: boolean) => void
	onFileInfo: (id: string) => void
	onMove?: (id: string, type: 'copy' | 'move') => void
	onConfigPermissions: (id: string) => void
	onConfigVersion: (id: string) => void
	onDownload: (id: string) => void
	onReclassify: (id: string) => void
	onChangeFilters: (newFilters: CustomColumnFilter[]) => void
	onCopyLink?: (id: string, type?: number) => void
	onAddNote?: (id: string) => void
	onSendToESign?: (id: string) => void
	checkedItems: {
		id: string
		type: number
	}[]
	onShareItem: (item: EditingItem) => void
	onRefresh?: () => Promise<void>
	lockedFiles: Record<string, boolean>
	columnFilters: CustomColumnFilter[]
	isSocketConnected?: boolean
	sortControllers: {
		orderBy: OrderByType
		orderDirection: OrderDirectionType
		toggleSorting: (columnId: OrderByType) => void
	}
	isMobile?: boolean
	workspace?: WorkspaceType
}

type TableColumn = ColumnDef<DataTableEntry, unknown> & {
	accessorKey?: keyof DataTableEntry
}

export const useCreateDefaultDataTable = ({
	data = [],
	currentFolderId,
	visibleColumns,
	selectedMobileColumn,
	containerRef,
	onToggleLock,
	onToggleItem,
	onToggleAll,
	onDeleteItem,
	onEditItem,
	onPreview,
	onFileInfo,
	onConfigPermissions,
	onConfigVersion,
	onDownload,
	onMove,
	onReclassify,
	onChangeFilters,
	onCopyLink,
	onAddNote,
	onSendToESign,
	onShareItem,
	onRefresh,
	columnFilters,
	checkedItems,
	lockedFiles,
	isSocketConnected,
	sortControllers,
	isMobile,
	workspace = 'personal',
}: useCreateDefaultDataTableProps) => {
	const navigate = useNavigate()
	const { hasFeature } = useBilling()
	const { formatDate, formatDateTime } = useUserDateFormat()

	const { currentUser } = useAuth()
	const { isApp } = useOutletContext<MainLayoutType>()
	const hideESignActions = Boolean(isApp || isMobile)

	const isRootFolder = useMemo(() => {
		return (
			currentFolderId &&
			currentUser?.organization?.id &&
			currentFolderId === currentUser?.organization?.rootFolderId
		)
	}, [currentUser?.organization?.rootFolderId, currentFolderId])

	const isVisibleFeature = useMemo(() => {
		if (!currentUser?.organization?.id) {
			return true
		}
		const isOwner = currentUser?.organization?.isOwner ?? false

		if (isRootFolder) {
			return isOwner
		}

		return true
	}, [currentUser, isRootFolder, currentFolderId, workspace])

	const { mutateAsync: toggleFavorite, isPending: isTogglingFavorite } =
		useToggleObjectFavorite()

	const renderSortingHeader = (
		title: string,
		column: Column<DataTableEntry, unknown>,
		className?: string,
		leftIcon?: ReactNode,
	) => {
		const isObject = [
			'modifiedBy',
			'createdBy',
			'category',
			'subcategory',
		]?.includes(column?.id)

		return (
			<div
				className={cn(
					'w-full flex group justify-start items-center gap-1 cursor-pointer select-none h-5 group pl-4 box-border  dark:text-text-table-dark min-h-full',
					className,
				)}
				onClick={() => {
					sortControllers?.toggleSorting(column?.id as OrderByType)
				}}
			>
				<FilterButton
					columnKey={column?.id}
					setColumnFilters={onChangeFilters}
					columnFilters={columnFilters}
					isDate={['modifiedAt', 'createdAt']?.includes(column?.id)}
					filterWindowClassName={cn(
						isApp && (column?.id === 'name' ? '-left-16' : '-left-[70px]'),
					)}
					isSize={column?.id === 'size'}
					objectType={
						['modifiedBy', 'createdBy']?.includes(column?.id)
							? 'user'
							: FILTERED_OBJECT_TYPE_VALUES?.includes(
										column?.id as FilteredObjectType,
								  )
								? (column.id as FilteredObjectType)
								: null
					}
					initialFilterType={
						isObject
							? FILTER_TYPES.OBJECT_ID
							: column?.id === 'processingStatus'
								? FILTER_TYPES.NUMBER
								: null
					}
					checkboxesItems={
						column?.id === 'processingStatus'
							? Object?.entries(processingStatuses)?.map(([k, v]) => ({
									id: k,
									label: v,
								}))
							: null
					}
				/>

				{/* {leftIcon} */}
				<EllipsisText
					tooltipLabel={title}
					text={title}
					leftIcon={leftIcon}
					className="text-center w-fit max-w-auto truncate"
				/>

				<span
					className={cn(
						'flex items-center justify-center transition-all duration-200 w-3 h-5',
						column?.id !== sortControllers.orderBy &&
							'hidden group-hover:inline-block',
					)}
				>
					{column?.id === sortControllers.orderBy &&
						sortControllers?.orderDirection === 'asc' && (
							<KeyboardArrowUp className="w-5 h-5" />
						)}
					{column?.id === sortControllers.orderBy &&
						sortControllers?.orderDirection === 'desc' && (
							<KeyboardArrowDown className="w-5 h-5" />
						)}
					{column?.id !== sortControllers.orderBy && (
						<UnfoldMore className="h-5 w-5 opacity-0 group-hover:opacity-100 duration-default -ml-1" />
					)}
				</span>
			</div>
		)
	}

	const handleEditDocument = (fileId: string, fileName?: string) => {
		openAuraEdit(fileId, fileName)
	}

	const handleToggleFavorite = async (item: {
		id: string
		type: number
		value: boolean
	}) => {
		await toggleFavorite({
			data: item,
			invalidate: true,
			folderId: currentFolderId ?? undefined,
		})
		await onRefresh?.()
	}

	const tableData = useMemo(() => {
		const safeData = Array.isArray(data) ? data : []
		return safeData?.map((entry) => ({
			id: entry.id,
			name: entry.name,
			createdBy: entry?.createdBy?.name || entry?.createdBy?.email,
			createdAt: entry?.createdAt || null,
			type: entry?.fileType,
			objectType: entry?.type,
			size: entry?.size,
			category: entry?.category?.name ? entry?.category?.name : '-',
			subcategory: entry?.subcategory?.name ? entry?.subcategory?.name : '-',
			modifiedBy: entry?.modifiedBy?.name || entry?.modifiedBy?.email || null,
			modifiedAt: entry.modifiedAt,
			processingStatus:
				entry?.processingStatus === 0 ? 2 : entry?.processingStatus || -1,
			lock: entry?.lock,
			options: {
				id: entry.id,
				isFavorite: entry?.isFavorite,
				isLocked:
					!!entry?.lock ||
					Boolean(
						typeof lockedFiles[entry?.id] === 'boolean'
							? lockedFiles[entry?.id]
							: entry?.lock?.id,
					),
				isSigned: entry?.isSigned,
			},
		}))
	}, [data, lockedFiles])

	const columns = useMemo<TableColumn[]>(() => {
		const base: TableColumn[] = [
			{
				accessorKey: 'name',
				enableResizing: true,
				enableSorting: true,
				minSize: 140,
				size: isMobile || isApp ? 140 : 200,
				header: ({ column }: { column: Column<DataTableEntry, unknown> }) => {
					const allIds = tableData
						?.filter(
							(row: DataTableEntry) =>
								!lockedFiles[row?.id] && !row?.options?.isLocked,
						)
						.map((row: DataTableEntry) => ({
							id: row.id,
							type: row.objectType,
							name: row.name,
							isFavorite: row.options?.isFavorite,
						}))

					const isAllSelected = Boolean(
						allIds?.length &&
						allIds?.every(({ id }) =>
							checkedItems?.find((item) => item?.id === id),
						),
					)
					return (
						<div className="h-full flex items-center gap-3 max-w-full pl-3 md:pl-6 group cursor-pointer">
							{isVisibleFeature && (
								<Checkbox
									checked={isAllSelected}
									disabled={!allIds?.length}
									onChange={() => onToggleAll?.(allIds, !isAllSelected)}
								/>
							)}
							{renderSortingHeader('Name', column, 'pl-2')}
						</div>
					)
				},
				cell: (props: CellContext<DataTableEntry, unknown>) => {
					const row = props.row
					const name = row?.original?.name || ''
					const isChecked = checkedItems?.some(
						({ id }) => id === row?.original?.id,
					)
					const isLocked = row?.original?.options?.isLocked
					const isSigned = row?.original?.options?.isSigned

					return (
						<div className="flex items-center gap-2 max-w-fit overflow-ellipsis">
							<div
								className="pl-3 md:pl-6 h-14 flex justify-center items-center"
								onClick={(e) => {
									e?.stopPropagation()
									if (isLocked) {
										Boolean(
											row?.original?.lock?.id === currentUser?.id ||
											currentUser?.organization?.isAdministrator,
										) && onToggleLock(row?.original?.id, false)
										return
									}
									onToggleItem?.(
										row.original.id,
										row.original.objectType,
										!isChecked,
										row.original?.name,
										row?.original?.type,
										row.original?.options?.isFavorite,
									)
								}}
							>
								{isLocked ? (
									<IconButton
										variant="secondary"
										className="w-5 h-5 min-w-5 min-h-5 mr-3 rounded-md p-0 bg-background-light-hover hover:bg-background-light-hover"
										icon={
											<Lock className="w-3.5 h-3.5 fill-text-secondary dark:fill-stroke-dark" />
										}
										tooltip={
											row?.original?.lock?.createdBy?.id
												? `Locked by ${
														row?.original?.lock?.createdBy?.id ===
														currentUser?.id
															? 'you'
															: (row?.original?.lock?.createdBy?.name ??
																row?.original?.lock?.createdBy?.email)
													}`
												: 'Unlock'
										}
										onClick={() => {
											if (
												row?.original?.lock?.id === currentUser?.id ||
												currentUser?.organization?.isAdministrator
											) {
												onToggleLock(row?.original?.id, false)
											}
										}}
										disabled={
											currentUser?.id !== row.original?.lock?.createdBy?.id &&
											!currentUser?.organization?.isAdministrator
										}
									/>
								) : (
									isVisibleFeature && (
										<Checkbox
											checked={isChecked}
											disabled={isLocked}
											onChange={(_, event) => {
												event?.stopPropagation()
												onToggleItem?.(
													row.original.id,
													row.original.objectType,
													!isChecked,
													row.original?.name,
													row?.original?.type,
													row.original?.options?.isFavorite,
												)
											}}
											className="mr-3"
										/>
									)
								)}
							</div>
							<FileFormatIcon
								extension={row?.original?.type}
								isSigned={isSigned}
							/>
							<Tooltip label={name} className={cn(isMobile && 'opacity-0')}>
								<p className="text-text-main  dark:text-text-table-dark truncate">
									{isMobile ? shortenFileName(name) : name}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'createdBy',
				enableResizing: true,
				enableSorting: true,
				minSize: 70,
				size: isApp ? 70 : 90,
				header: ({ column }: { column: Column<DataTableEntry, unknown> }) =>
					renderSortingHeader('Creator', column),
				cell: (props: CellContext<DataTableEntry, unknown>) => (
					<p className="truncate">{String(props.getValue() ?? '')}</p>
				),
			},
			{
				accessorKey: 'createdAt',
				enableResizing: true,
				enableSorting: true,
				minSize: 120,
				size: 120,
				header: ({ column }: { column: Column<DataTableEntry, unknown> }) =>
					renderSortingHeader('Creation Date', column),
				cell: (info: CellContext<DataTableEntry, unknown>) => {
					const val = info.getValue()
					if (!val) return '-'
					const str = String(val)
					return (
						<Tooltip
							label={formatDateTime(str)}
							classNameChildrenWrapper="w-fit"
						>
							<span>{formatDate(str)}</span>
						</Tooltip>
					)
				},
			},
			{
				accessorKey: 'modifiedAt',
				enableResizing: true,
				enableSorting: true,
				minSize: 120,
				size: 120,
				header: ({ column }) => renderSortingHeader('Modified Date', column),
				cell: (info) => {
					const val = info.getValue()
					if (!val) return <span className="dark:text-text-table-dark">-</span>
					const str = String(val)
					return (
						<span className="dark:text-text-table-dark">
							<Tooltip
								label={formatDateTime(str)}
								classNameChildrenWrapper="w-fit"
							>
								<span>{formatDate(str)}</span>
							</Tooltip>
						</span>
					)
				},
			},
			{
				accessorKey: 'modifiedBy',
				enableResizing: true,
				enableSorting: true,
				minSize: 90,
				size: isApp ? 90 : 110,
				header: ({ column }: { column: Column<DataTableEntry, unknown> }) =>
					renderSortingHeader('Modifier', column),
				cell: (props: CellContext<DataTableEntry, unknown>) => (
					<p className="truncate dark:text-text-table-dark">
						{String(props.getValue() ?? '')}
					</p>
				),
			},
			{
				accessorKey: 'processingStatus',
				enableResizing: true,
				enableSorting: true,
				minSize: 80,
				size: 80,
				header: ({ column }) =>
					renderSortingHeader(
						'Status',
						column,
						'',
						<AutoAwesome className="w-4 h-4" />,
					),
				cell: ProcessingStatusCell,
			},
			{
				accessorKey: 'category',
				enableResizing: true,
				enableSorting: true,
				minSize: 90,
				size: isApp ? 90 : 120,
				header: ({ column }: { column: Column<DataTableEntry, unknown> }) =>
					renderSortingHeader('Category', column),
				cell: (props: CellContext<DataTableEntry, unknown>) => (
					<Tooltip
						label={String(props.getValue() ?? '')}
						className={cn(isMobile && 'opacity-0')}
					>
						<p className="capitalize truncate dark:text-text-table-dark">
							{String(props.getValue() ?? '')}
						</p>
					</Tooltip>
				),
			},
			{
				accessorKey: 'subcategory',
				enableResizing: true,
				enableSorting: true,
				minSize: 90,
				size: isApp ? 90 : 120,
				header: ({ column }: { column: Column<DataTableEntry, unknown> }) =>
					renderSortingHeader('Sub Category', column),
				cell: (props: CellContext<DataTableEntry, unknown>) => (
					<Tooltip
						label={String(props.getValue() ?? '')}
						className={cn(isMobile && 'opacity-0')}
					>
						<p className="capitalize truncate dark:text-text-table-dark">
							{String(props.getValue() ?? '')}
						</p>
					</Tooltip>
				),
			},
			{
				accessorKey: 'type',
				enableResizing: true,
				enableSorting: true,
				minSize: 90,
				size: 90,
				header: ({ column }: { column: Column<DataTableEntry, unknown> }) =>
					renderSortingHeader('Type', column),
				cell: (props: CellContext<DataTableEntry, unknown>) =>
					String(props.getValue() ?? ''),
			},
			{
				accessorKey: 'size',
				enableResizing: true,
				enableSorting: true,
				minSize: 80,
				size: 80,
				header: ({ column }: { column: Column<DataTableEntry, unknown> }) =>
					renderSortingHeader('Size', column),
				cell: (props: CellContext<DataTableEntry, unknown>) =>
					String(formatDocumentSize(Number(props.getValue())) || '-'),
			},
			{
				accessorKey: 'options',
				enableResizing: false,
				enableSorting: false,
				size: isMobile || columnFilters?.length ? 50 : 30,
				maxSize: 50,
				minSize: 25,
				header: () => (
					<div className="flex items-center justify-end mr-2 gap-2 overflow-hidden min-h-full">
						{!isApp && (
							<IconButton
								variant="transparent"
								className="max-w-[20px] max-h-[20px] w-5 h-5 p-0 group hover:bg-transparent"
								tooltipPosition="left"
								icon={
									<ResetColumnsSize className="fill-text-table w-full h-full group-hover:fill-accent-hover" />
								}
								onClick={() => table.resetColumnSizing()}
								tooltip={'Reset columns sizes'}
							/>
						)}
						{!!columnFilters?.length && (
							<IconButton
								variant="transparent"
								className="max-w-[20px] max-h-[20px] w-5 h-5 p-0 group hover:bg-transparent"
								tooltipPosition="left"
								icon={
									<FilterAltOff className="fill-text-table w-full h-full group-hover:fill-accent-hover" />
								}
								onClick={() => onChangeFilters([])}
								tooltip={'Clean filters'}
							/>
						)}
					</div>
				),
				cell: ({ row }: { row: Row<DataTableEntry> }) => {
					if (!isVisibleFeature) return null

					const { id, isFavorite, type, name, isLocked, size, isSigned } = {
						id: row.original.id,
						isFavorite: row.original.options?.isFavorite,
						type: row?.original?.objectType,
						name: row?.original?.name,
						isLocked:
							row?.original?.options?.isLocked || lockedFiles[row.original.id],
						size: row?.original?.size,
						isSigned: row?.original?.options?.isSigned,
					}

					const isFileHasContent = Boolean(!type && size > 0)
					const isCommonItem = Boolean(type || isFileHasContent)
					const canSendToESign = isFileNameEsignSupported(name)

					return (
						<div className="bg-inherit md:absolute right-0 flex items-center justify-end gap-2 w-fit md:ml-auto pl-5 h-full group-hover:lg:left-custom-shadow mr-10">
							<div
								className={cn(
									isApp
										? 'hidden'
										: 'items-center justify-end hidden md:flex gap-2 opacity-0 lg:group-hover:opacity-100 transition-all duration-150 ease-in-out',
								)}
							>
								{(isCommonItem
									? hasFeature(Features.Basic)
									: !type &&
										!size &&
										!isApp &&
										hasFeature(Features.Versions)) && (
									<IconButton
										className="w-8 h-8 rounded-lg"
										variant="secondary"
										icon={
											isCommonItem || !hasFeature(Features.Versions) ? (
												<Visibility className="w-4 dark:fill-text-main-dark" />
											) : (
												<Edit className="w-4 dark:fill-text-main-dark" />
											)
										}
										onClick={() =>
											!type && !size && !isApp && hasFeature(Features.Versions)
												? handleEditDocument(row.original.id, row.original.name)
												: onPreview(id)
										}
										tooltip={
											type
												? 'Open folder'
												: size
													? 'Preview file'
													: hasFeature(Features.Versions)
														? 'Edit file'
														: 'Preview file'
										}
									/>
								)}
								{hasFeature(Features.Basic) && isVisibleFeature && (
									<IconButton
										className="w-8 h-8 rounded-lg"
										variant={isFavorite ? 'primary' : 'secondary'}
										icon={<Star className="w-4 dark:fill-text-main-dark" />}
										tooltip={
											isFavorite ? 'Remove from favorites' : 'Add to favorites'
										}
										loading={isTogglingFavorite}
										onClick={() => {
											handleToggleFavorite({
												id,
												type,
												value: !isFavorite,
											})
										}}
									/>
								)}
								{hasFeature(Features.Basic) && isVisibleFeature && (
									<IconButton
										className="w-8 h-8 rounded-lg"
										variant="secondary"
										icon={<Delete className="w-4 dark:fill-text-main-dark" />}
										tooltip="Delete"
										disabled={isLocked}
										onClick={() =>
											onDeleteItem({
												id,
												name,
												type,
											})
										}
									/>
								)}
							</div>
							{isVisibleFeature && (
								<OptionsMenuButton
									buttonClassName="ml-auto"
									parentScrollRef={containerRef}
									items={[
										{
											icon: <Visibility />,
											label: type ? 'Open folder' : 'Open in new tab',
											onClick: () => onPreview(id, null, true),
											visible:
												hasFeature(Features.Basic) && isCommonItem && !isApp,
										},
										{
											icon: <Visibility />,
											label: 'Open',
											onClick: () => onPreview(id, null),
											visible:
												hasFeature(Features.Basic) &&
												isFileHasContent &&
												isMobile,
										},
										{
											icon: <Edit />,
											label: 'Edit',
											onClick: () =>
												handleEditDocument(row.original.id, row.original.name),
											visible:
												!type &&
												hasFeature(Features.Versions) &&
												!isApp &&
												!isSigned,
											disabled: isLocked,
										},
										{
											icon: <LocalOffer />,
											label: 'Rename',
											onClick: () =>
												onEditItem({
													id,
													name: row?.original?.name,
													type: row?.original?.objectType,
												}),
											visible: hasFeature(Features.Basic),
											disabled: isLocked,
										},
										{
											icon: <Delete />,
											label: 'Delete',
											onClick: () =>
												onDeleteItem({
													id,
													name: row?.original?.name,
													type: row?.original?.objectType,
												}),
											visible: hasFeature(Features.Basic),
											disabled: isLocked,
											withDivider: true,
										},
										{
											icon: <InfoOutlined />,
											label: 'File info',
											onClick: () => onFileInfo(id),
											visible: !type,
										},
										{
											icon: <ManageHistory />,
											label: 'Versions',
											onClick: () => onConfigVersion(id),
											visible:
												hasFeature(Features.Versions) && isFileHasContent,
										},
										{
											icon: isFavorite ? <Star /> : <StarOutlineOutlined />,
											label: isFavorite ? 'Unstar' : 'Star',
											onClick: () =>
												handleToggleFavorite({
													id,
													type: row?.original?.objectType,
													value: !isFavorite,
												}),
											visible: true,
										},
										{
											icon: <Key />,
											label: 'Permissions',
											onClick: () => onConfigPermissions(id),
											visible: hasFeature(
												Features.ManageGroupsRolesPermissions,
											),
											disabled: isLocked,
											withDivider: !type,
										},
										{
											icon: <Refresh />,
											label: 'Reclassify',
											onClick: () => onReclassify(id),
											visible:
												hasFeature(Features.Classification) &&
												!isRootFolder &&
												isCommonItem,
											disabled: isLocked,
										},
										{
											icon: <StickyNote2Sharp />,
											label: 'Notes',
											onClick: () => {
												onAddNote?.(id)
											},
											disabled: !onAddNote,
											visible: !type && hasFeature(Features.Collaboration),
										},
										{
											icon: <IosShareOutlined />,
											label: 'Share',
											onClick: () => onShareItem({ id, name, type }),
											visible: hasFeature(Features.Basic) && isFileHasContent,
											withDivider: !type && isSigned,
										},
										{
											icon: (
												<SigningSymbol className="w-5 h-5 text-text-secondary dark:text-text-main-dark" />
											),
											label: 'Send to eSign',
											onClick: () => onSendToESign?.(id),
											visible:
												hasFeature(Features.Basic) &&
												!type &&
												!isSigned &&
												canSendToESign &&
												!hideESignActions,
											withDivider: true,
										},
										{
											icon: <FileDownloadOutlined />,
											label: 'Download',
											onClick: () => onDownload(id),
											visible: hasFeature(Features.Basic) && isCommonItem,
											withDivider: !!type,
										},
										{
											icon: <CopyAllOutlined />,
											label: 'Copy',
											onClick: () => {
												onMove?.(id, 'copy')
											},
											visible:
												hasFeature(Features.Basic) && isCommonItem && !isSigned,
											disabled: isLocked,
										},
										{
											icon: <DriveFileMoveOutlined />,
											label: 'Move',
											onClick: () => {
												onMove?.(id, 'move')
											},
											visible: hasFeature(Features.Basic) && isCommonItem,
											disabled: isLocked,
										},
										{
											icon: <Link />,
											label: 'Copy link',
											onClick: () => {
												onCopyLink?.(id, type)
											},
											disabled: !onCopyLink,
											visible: hasFeature(Features.Basic),
										},
									]}
								/>
							)}
						</div>
					)
				},
			},
		]

		if (isMobile) {
			return base.filter((columnDef) => {
				const accessorKey = columnDef.accessorKey as string | undefined
				const available = ['id', 'name', 'options']
				selectedMobileColumn && available?.push(selectedMobileColumn)
				return accessorKey && available.includes(accessorKey)
			})
		}

		return base
	}, [
		tableData,
		checkedItems,
		lockedFiles,
		columnFilters,
		selectedMobileColumn,
		isMobile,
		sortControllers,
		isVisibleFeature,
		currentUser,
		isApp,
		hideESignActions,
		onSendToESign,
	])

	const table = useReactTable({
		data: tableData,
		columns,
		columnResizeMode: 'onChange',
		getCoreRowModel: getCoreRowModel(),
	})

	useEffect(() => {
		if (isMobile) return
		table.getAllColumns().forEach((column) => {
			table
				.getColumn(column.id)
				?.toggleVisibility(visibleColumns.includes(column.id))
		})
	}, [visibleColumns, table, isMobile])

	return { table }
}
